import { Combobox, Transition } from "@headlessui/react";
import { SearchIcon, XIcon } from "@heroicons/react/outline";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { Fragment, useCallback, useMemo, useState } from "react";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Option } from "@/queries/models/types";

interface ParameterMultiSelectProps {
  options: Option[];
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  readOnly?: boolean;
  isPreview?: boolean;
}

export const ParameterMultiSelect = ({
  options,
  value = [],
  onChange,
  placeholder = "Select multiple options",
  isDisabled,
  readOnly,
  isPreview,
}: ParameterMultiSelectProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    return options.filter(option =>
      option.display_name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  const handleToggleOption = useCallback(
    (optionValue: string) => {
      if (isPreview) {
        return;
      }

      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(optionValue);

      if (isSelected) {
        onChange(currentValues.filter(v => v !== optionValue));
      } else {
        onChange([...currentValues, optionValue]);
      }
    },
    [value, onChange, isPreview],
  );

  const handleRemoveOption = useCallback(
    (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPreview || readOnly || isDisabled) {
        return;
      }

      const currentValues = Array.isArray(value) ? value : [];
      onChange(currentValues.filter(v => v !== optionValue));
    },
    [value, onChange, isPreview, readOnly, isDisabled],
  );

  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPreview || readOnly || isDisabled) {
        return;
      }
      onChange([]);
    },
    [onChange, isPreview, readOnly, isDisabled],
  );

  const selectedOptions = useMemo(() => {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map(v => options.find(opt => opt.value === v)).filter(Boolean) as Option[];
  }, [value, options]);

  return (
    <div className="relative">
      <Combobox value={value} onChange={() => undefined} disabled={isDisabled || readOnly} multiple>
        <Combobox.Button
          onClick={() => !isDisabled && !readOnly && setIsOpen(!isOpen)}
          className={cn(
            "flex min-h-[36px] w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            selectedOptions.length > 0 ? "pr-2" : "",
          )}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map(option => (
                <Badge
                  key={option.value}
                  variant={BadgeVariant.GRAY}
                  className="flex items-center gap-1"
                >
                  <span className="text-xs">{option.display_name}</span>
                  {!readOnly && !isDisabled && (
                    <XIcon
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={e => handleRemoveOption(String(option.value), e)}
                    />
                  )}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <div className="ml-2 flex items-center gap-1">
            {selectedOptions.length > 0 && !readOnly && !isDisabled && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleClearAll}
                size="tinyIcon"
                className="h-4 w-4"
              >
                <XIcon className="h-3 w-3" />
              </Button>
            )}
            <CaretSortIcon className="h-4 w-4 flex-shrink-0 opacity-50" />
          </div>
        </Combobox.Button>

        <Transition
          show={isOpen}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          afterLeave={() => setSearchQuery("")}
        >
          <Combobox.Options
            static
            className="absolute left-0 right-0 z-50 mt-1 max-h-80 min-w-0 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            {options.length > 5 && (
              <div className="sticky top-0 z-10 border-b bg-popover/95 px-3 py-2 backdrop-blur-sm">
                <div className="relative">
                  <Input
                    placeholder="Search..."
                    className="h-8 w-full pl-8 pr-3 text-sm"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  />
                  <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
            )}

            <div className="max-h-64 overflow-y-auto p-1">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No options found
                </div>
              ) : (
                filteredOptions.map(option => {
                  const isSelected = value?.includes(String(option.value));
                  return (
                    <div
                      key={option.value}
                      onClick={() => handleToggleOption(String(option.value))}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 pr-8 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-gray-100",
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center">
                        <span className="truncate">{option.display_name}</span>
                      </div>
                      {isSelected && (
                        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                          <CheckIcon className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
};
